import React, { forwardRef, MouseEventHandler } from 'react';
import classnames from 'classnames';
import Icon from '../Icon';
import { ICON_POSITIONS } from './constants';
import classes from './index.module.scss';
import { IconPositionType, IconType, InputProps } from './types';

interface IconProps {
    className?: string;
    iconPosition: IconPositionType;
    size: number;
    children: IconType;
    onClick?: MouseEventHandler<HTMLButtonElement>;
}

const generateIcon = (
    iconPosition: IconPositionType,
    icon?: IconType,
    iconClassName?: string,
    onIconClick?: MouseEventHandler<HTMLButtonElement>
) => {
    if (!icon) {
        return null;
    }

    const className = classnames(
        classes.icon,
        iconClassName
    );

    if (!React.isValidElement(icon)) {
        const props: IconProps = {
            className,
            iconPosition,
            size: 14,
            children: icon
        };

        if (typeof onIconClick === 'function') {
            props.onClick = onIconClick;
        }

        return <Icon {...props}/>;
    }

    const isIconComponent = icon.type === Icon;

    if (!isIconComponent) {
        return null;
    }

    return React.cloneElement(icon, {
        className: classnames(icon.className, className)
    });
};

/**
 * @deprecated Please use Input from @fiverr-private/forms instead.
 */
const Input = forwardRef<HTMLInputElement, InputProps>(({
    type = 'text',
    className: customClassName,
    inputClassName: customInputClassName,
    iconClassName,
    disabled = false,
    icon,
    iconPosition = 'start',
    onIconClick,
    innerRef,
    isSuccess = false,
    isError = false,
    hideNumberSpinButton = false,
    ...rest
}, ref) => {
    const className = classnames(
        classes.inputWrapper,
        {
            [classes.disabled]: disabled,
            [classes.hasIcon]: icon,
            [classes.iconStart]: iconPosition === 'start',
            [classes.success]: isSuccess,
            [classes.error]: isError,
            [classes.hideNumberSpinButton]: type === 'number' && hideNumberSpinButton
        },
        customClassName
    );

    const inputClassName = classnames(
        classes.input,
        customInputClassName
    );

    const inputProps = {
        disabled,
        type,
        ref: innerRef || ref,
        ...rest
    };

    return (
        <div className={className}>
            <div className={classes.innerWrapper}>
                <input className={inputClassName} {...inputProps}/>
                {generateIcon(iconPosition, icon, iconClassName, onIconClick)}
            </div>
        </div>
    );
});

Input.displayName = 'Input';

Object.assign(Input, {
    ICON_POSITIONS
});

export default Input;
